import * as React from 'react';
import { Typography, Box, Button } from "@mui/material";
import { PlantRow } from ".";
import { Interval, SeasonPlantingPlan, FrostDates, PlantType } from "../Types";
import { Dayjs } from 'dayjs';
import { useContext, useEffect, useRef } from 'react';
import { SelectedPlantTypeContext } from '../AppContext';

export function GridCellDate({ day, offset }: { day: Dayjs, offset: number }) {
    return <Typography component='span' fontSize='1.25em' fontWeight='bold' letterSpacing='1px'>{day.format('MM/DD')}</Typography>;
}
export function CropCell({ crop, id }: { crop: string, id: string}) {
  const {selectedPlantType} = useContext(SelectedPlantTypeContext);
  const cropCell = useRef<HTMLDivElement>(
    null
  );
  useEffect(() => {
    if (selectedPlantType
      && selectedPlantType.id === id
      && cropCell.current) {
      console.log("scrolling to crop cell");
      cropCell.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [selectedPlantType, cropCell]);
  return (
    <Typography ref={cropCell} id={id} fontSize="1.25em" fontWeight="bold">
      {crop}
    </Typography>
  );
}
export function ColdStratifyCell({ row: { canBeStratified, coldStratify } }: {row: PlantRow}) {
    if (!canBeStratified) return <Typography fontSize='1.25em'>Not Needed</Typography>
    const { min, max } = coldStratify;
    return <Typography fontSize='1.25em' fontWeight='bold'>{min} - {max} Weeks</Typography>;
}
export function IndoorStartCell({ plantingPlan, frostDates, row, alignLeft }: { plantingPlan: SeasonPlantingPlan, frostDates: FrostDates, row: PlantRow, alignLeft?: boolean }) {
    const GridCellDateRange = ({ day, firstOffset, lastOffset, directSow }: { day: Dayjs, firstOffset: number, lastOffset, directSow: boolean }) => {
        var firstDay = day.add(firstOffset, 'days');
        var lastDay = day.add(lastOffset, 'days');
        return <Box sx={{ display: 'flex', justifyItems: 'left', flexDirection: 'column', width: '100%', textAlign: 'left' }}>
            <Typography textAlign={alignLeft ? 'left' : 'left'} fontSize='1.25em' fontWeight='bold' letterSpacing='1px'>{firstDay.format('MM/DD')} - {lastDay.format('MM/DD')}</Typography>
            {directSow ? <Typography textAlign={'left'} fontSize='1em' fontStyle='italic'>* Can be directly sown.</Typography> : ''}
        </Box>;
    }
    const { earliestIndoorOffset, latestIndoorOffset, directSow, daysToMature } = row;
    if (plantingPlan === SeasonPlantingPlan.Spring) {
        if (!earliestIndoorOffset || !latestIndoorOffset) {
            return <span aria-description='No information.' ></span>
        }
        return <GridCellDateRange
            day={frostDates.lastDate.day}
            firstOffset={earliestIndoorOffset}
            lastOffset={latestIndoorOffset}
            directSow={directSow} />;
    } else {
        if (!daysToMature || !earliestIndoorOffset || !latestIndoorOffset) {
            return <span aria-description='No information.' ></span>
        }
        const { max } = daysToMature;
        return <GridCellDateRange
            day={frostDates.firstDate.day}
            firstOffset={0 - max + earliestIndoorOffset}
            lastOffset={0 - max + latestIndoorOffset}
            directSow={directSow} />;
    }

}
export function OutdoorPlantingCell({ row, plantingPlan, frostDates }: { row: PlantRow, plantingPlan: SeasonPlantingPlan, frostDates: FrostDates }) {
    if (plantingPlan === SeasonPlantingPlan.Spring) {
        if (!Number.isInteger(row.springPlantingOffset)) {
            return <span aria-description='No information.' ></span>
        } else {
            const offset = row.springPlantingOffset;
            const day = frostDates.lastDate.day.add(offset, 'days');
            return <GridCellDate day={day} offset={offset} />
        }
    } else {
        if (!row.daysToMature) {
            return <span aria-description='No information.' ></span>
        } else {
            const { max } = row.daysToMature;
            const offset = 0 - max;
            const day = frostDates.firstDate.day.add(offset, 'days');
            return <GridCellDate day={day} offset={offset} />
        }
    }

}
export function PlantSpacingCell({ row }: { row: PlantRow }) {
    if (!row.plantSpacing) {
        return <span aria-description='No information'></span>
    } else {
        let minSpacing: number | string = row.plantSpacing.min;
        const maxSpacing = row.plantSpacing.max;
        const minAndMaxAreEqual = minSpacing === maxSpacing;
        if (row.broadcastSow) return <Typography fontSize='1.25em'>Broadcast Sow</Typography>
        if (Number(minSpacing) < 1) minSpacing = "1/2";
        return minAndMaxAreEqual ? <Typography fontSize='1.25em'>{`${minSpacing} in.`}</Typography> : <Typography fontSize='1.25em'>{`${minSpacing} to ${maxSpacing} in.`}</Typography>;
    }
}
export function StoreProductsCell({ row, setSelectedPlantType }: { row: PlantRow, setSelectedPlantType: (plantType: PlantType) => void }) {
  return <Button variant='outlined' onClick={() => {
    setSelectedPlantType(row);
  }}>
      View Products
    </Button>
}